import { LngLat } from 'mapbox-gl';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useBodyContainerClassName from "../../hooks/useBodyContainerClassName";
import useProperty from '../../hooks/useProperty';
import usePropertyAiGrade from '../../hooks/usePropertyAiGrade';
import usePropertyEstimates from "../../hooks/usePropertyEstimates";
import { formatNumber, formatPrice } from '../../utils/utils';
import commonStyles from "../Common.module.css";
import detailsStyles from "../Details.module.css";
import FieldRow from "../FieldRow";
import tabStyles from "../List.module.css";
import popupStyles from "../Popup.module.css";
import Spinner from '../spinners/Spinner';
import styles from "./PropertyAiGrade.module.css";
import { MonthlyRentalComparableCompsInfo, SalePricePerAcreComparableCompsInfo } from "./PropertyEstimatesCompsPanel";
import PropertyIndustrialScarcityRow from "./PropertyIndustrialScarcityRow";

function PropertyDetailsAiGradeTab({ visible }: IPropertyDetailsAiGradeTabProps) {
    const { longitude, latitude } = useParams();
    const coordinates = useMemo(() => new LngLat(Number(longitude), Number(latitude)), [longitude, latitude]);
    const property = useProperty(coordinates).property!;
    const { aiGrade, isLoading, isError, fetchAiGrade } = usePropertyAiGrade(property)
    const bodyContainerClassName = useBodyContainerClassName();
    const propertyEstimates = usePropertyEstimates(property);

    useEffect(() => {
        if (!visible) return;
        if (aiGrade || isLoading || isError) return;

        fetchAiGrade();
    }, [visible, aiGrade, isLoading, isError, fetchAiGrade]);

    if (!visible)
        return null;

    return (
        <>
            {isLoading && <Spinner className={tabStyles.spinner} />}
            {aiGrade && (
                <div className={bodyContainerClassName}>
                    <div className={detailsStyles.columns}>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Summary</div>
                            <FieldRow label="PROPERTY GRADE">
                                {aiGrade.grade}
                            </FieldRow>
                            <FieldRow label="LOCATION GRADE">
                                {propertyEstimates.isGradeLoading
                                    ? <Spinner />
                                    : propertyEstimates.locationGrade}
                            </FieldRow>
                            <PropertyIndustrialScarcityRow centerPoint={property.centerPoint} />
                        </div>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Estimates</div>
                            {!(propertyEstimates.monthlyRental || propertyEstimates.salePricePerAcre) &&
                                <div className={commonStyles.key}>Not enough data available</div>
                            }
                            <FieldRow label="USABLE AREA">
                                {aiGrade.usableArea ? `${formatNumber(aiGrade.usableArea)} Acres` : null}
                            </FieldRow>
                            <FieldRow label="MONTHLY RENTAL RATE">
                                {propertyEstimates.monthlyRental && (
                                    <>
                                        {`${formatPrice(propertyEstimates.monthlyRental)}/Acre`}
                                        {propertyEstimates.isMonthlyRentalLimited && " * Limited Comps"}
                                    </>
                                )}
                            </FieldRow>
                            <MonthlyRentalComparableCompsInfo data={propertyEstimates.monthlyRentalComparableComps} />
                            <FieldRow label="SALE PRICE PER USABLE ACRE">
                                {propertyEstimates.salePricePerAcre && (
                                    <>
                                        {formatPrice(propertyEstimates.salePricePerAcre)}
                                        {propertyEstimates.isSalePricePerAcreLimited && " * Limited Comps"}
                                    </>
                                )}
                            </FieldRow>
                            <SalePricePerAcreComparableCompsInfo data={propertyEstimates.salePricePerAcreComparableComps} />
                        </div>
                        <div className={[detailsStyles.column, commonStyles.rowSeparated].join(" ")}>
                            <div className={commonStyles.header}>Valuation</div>
                            <FieldRow label="SALE COMP METHOD">
                                {propertyEstimates.salePricePerAcre && aiGrade.usableArea
                                    ? formatPrice(propertyEstimates.salePricePerAcre * aiGrade.usableArea)
                                    : null
                                }
                            </FieldRow>
                            <FieldRow label="INCOME METHOD">
                                {propertyEstimates.monthlyRental && aiGrade.usableArea
                                    ? formatPrice(propertyEstimates.monthlyRental * aiGrade.usableArea * 12 / 0.08)
                                    : null
                                }
                            </FieldRow>
                        </div>
                    </div>
                    <div className={detailsStyles.columns}>
                        <div className={detailsStyles.column}>
                            <div className={commonStyles.header}>Details</div>
                            <div className={styles.fullaiGradeBox}>
                                {aiGrade.reasoning}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!aiGrade && isError && (
                <>
                    <div className={popupStyles.header}>Ai Grade</div>
                    <div className={popupStyles.body}>No ai grade information found</div>
                </>
            )}
        </>
    );
}

export default observer(PropertyDetailsAiGradeTab);

interface IPropertyDetailsAiGradeTabProps {
    visible: boolean;
}